/**
 * Employee Service
 * Handles all employee-related API calls
 * @module services/employees/employeeService
 */

import httpClient from '@/utils/httpClient';
import { TENANT_ID } from '@/config/constants';
import { isValidId, createInvalidIdResponse } from '@/services/shared/validationHelpers';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';

const employeeService = {
  approve: async (id) => {
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.APPROVE, id));
      return response.data;
    } catch (error) {
      console.error('Error approving employee:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  reject: async (id, rejectionReason) => {
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.REJECT, id), {
        approvalStatus: 'rejected',
        rejectionReason
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting employee:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params.append(key, filters[key]);
        }
      });
      const endpoint = `${getEndpointPath(ENDPOINTS.EMPLOYEES.BASE)}?${params}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.warn('Error fetching employees, using fallback data:', error.message);
      return {
        success: true,
        data: [],
        message: 'Fallback data - database unavailable',
        pagination: {
          total: 0,
          page: 1,
          limit: 10,
          pages: 1
        },
        fromCache: true
      };
    }
  },

  getMe: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.EMPLOYEES.ME));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getById: async (id) => {
    if (!isValidId(id, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.EMPLOYEES.BY_ID, id));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  create: async (employeeData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.EMPLOYEES.CREATE), employeeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  update: async (id, changedFields) => {
    if (!isValidId(id, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      if (Object.keys(changedFields).length === 0) {
        console.warn('⚠️ No fields to update');
        return {
          success: true,
          message: 'No changes to update',
          data: {}
        };
      }

      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.UPDATE, id), changedFields);
      return response.data;
    } catch (error) {
      console.error('Error updating employee:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  delete: async (id) => {
    if (!isValidId(id, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.EMPLOYEES.DELETE, id));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getHolidayRequests: async (employeeId) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.EMPLOYEES.REQUESTS_BY_EMPLOYEE, employeeId));
      return response.data;
    } catch (error) {
      console.error('Error fetching holiday requests:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getNotifications: async (employeeId) => {
    try {
      if (!employeeId) {
        console.warn('⚠️ No employee ID provided');
        return {
          success: true,
          data: {
            notifications: [],
            total: 0,
            unread: 0
          }
        };
      }

      const response = await httpClient.get(getEndpointPath(ENDPOINTS.EMPLOYEES.NOTIFICATIONS, employeeId));
      
      if (response.data && response.data.success) {
        return response.data;
      } else {
        return {
          success: true,
          data: {
            notifications: response.data?.data?.notifications || response.data?.notifications || [],
            total: response.data?.data?.total || response.data?.total || 0,
            unread: response.data?.data?.unread || response.data?.unread || 0
          }
        };
      }
    } catch (error) {
      console.error('Error fetching notifications:', error.response?.data || error.message);
      return {
        success: false,
        error: error.message || 'Error fetching notifications',
        data: {
          notifications: [],
          total: 0,
          unread: 0
        }
      };
    }
  },

  markNotificationAsRead: async (employeeId, notificationId) => {
    if (!isValidId(employeeId, 'Employee') || !isValidId(notificationId, 'Notification')) {
      return createInvalidIdResponse('Employee/Notification');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.MARK_NOTIFICATION_READ, employeeId, notificationId));
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  markAllNotificationsAsRead: async (employeeId) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.MARK_ALL_NOTIFICATIONS_READ, employeeId));
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  createHolidayRequest: async (requestData) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.EMPLOYEES.REQUESTS), requestData);
      return response.data;
    } catch (error) {
      console.error('Error creating holiday request:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  approveHolidayRequest: async (employeeId, requestId) => {
    if (!isValidId(employeeId, 'Employee') || !isValidId(requestId, 'Request')) {
      return createInvalidIdResponse('Employee/Request');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.APPROVE_REQUEST, employeeId, requestId));
      return response.data;
    } catch (error) {
      console.error('Error approving holiday request:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  rejectHolidayRequest: async (employeeId, requestId, rejectionReason) => {
    if (!isValidId(employeeId, 'Employee') || !isValidId(requestId, 'Request')) {
      return createInvalidIdResponse('Employee/Request');
    }
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.EMPLOYEES.REJECT_REQUEST, employeeId, requestId), { 
        rejectionReason 
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting holiday request:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  uploadDocument: async (id, file, description = '') => {
    if (!isValidId(id, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.EMPLOYEES.DOCUMENTS), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Tenant-ID': TENANT_ID,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  getDocument: async (id) => {
    if (!isValidId(id, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.EMPLOYEES.DOCUMENTS_BY_ID, id));
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  deleteDocument: async (publicId) => {
    if (!publicId) {
      throw { success: false, message: 'Document public ID is required' };
    }
    try {
      // Use upload delete endpoint (removes from storage + all employee records)
      // Keep path slashes so Express `/:publicId(*)` can match R2 keys
      const encodedId = String(publicId)
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.UPLOAD.DELETE, encodedId));
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Allowances
  createAllowance: async (employeeId, allowanceData) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.EMPLOYEES.ALLOWANCES, employeeId),
        allowanceData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updateAllowance: async (employeeId, allowanceId, allowanceData) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    if (!isValidId(allowanceId, 'Allowance')) {
      return createInvalidIdResponse('Allowance');
    }
    try {
      const endpoint = ENDPOINTS.EMPLOYEES.UPDATE_ALLOWANCE(employeeId, allowanceId);
      const response = await httpClient.patch(endpoint, allowanceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  deleteAllowance: async (employeeId, allowanceId) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    if (!isValidId(allowanceId, 'Allowance')) {
      return createInvalidIdResponse('Allowance');
    }
    try {
      const endpoint = ENDPOINTS.EMPLOYEES.DELETE_ALLOWANCE(employeeId, allowanceId);
      const response = await httpClient.delete(endpoint);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  renewContract: async (employeeId, contractDurationMonths) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.EMPLOYEES.RENEW_CONTRACT, employeeId),
        { contractDurationMonths }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  renewResidence: async (employeeId, durationMonths) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.EMPLOYEES.RENEW_RESIDENCE, employeeId),
        { durationMonths }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default employeeService;

